import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/did.py list_did_complaints / complaint_summary.
export interface ComplaintHit {
  complaint_id: number;
  source: string;                 // "fcc" | "ftc"
  source_record_id: string;
  phone: string;
  did_id: number | null;
  owner_usr_id: number | null;
  username: string | null;
  issue_date: string;             // date the complained-about call happened
  complaint_type: string | null;
  issue: string | null;
  state: string | null;
  during_ownership: boolean | null; // true = inside our ownership window; false = another owner's; null = date unknown
  action: string;                 // quarantine | prior_owner | already_quarantined | recorded
  detected_at: string;
  acquired_at: string | null;     // did.created_at
  first_used_at: string | null;   // first time we dialed on it
  after_first_use: boolean | null;
  days_after_acquired: number | null;
  did_status: "active" | "quarantined" | "retired" | "deleted" | "unknown";
}

export interface ComplaintListParams {
  owner?: number;
  ours_only?: boolean;
  days?: number;
  source?: string;
  action?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface ComplaintList {
  total: number;
  limit: number;
  offset: number;
  complaints: ComplaintHit[];
}

export interface ComplaintSummary {
  filters: { ours_only: boolean; days: number; source: string | null; owner: number | null };
  totals: {
    hits: number; ours: number; prior_owner: number; undated: number; dids_ours: number; owners_ours: number;
    quarantined_by_sweep: number; released: number; ours_7d: number; ours_30d: number;
    still_active_dids: number; last_sweep_hit: string | null;
  };
  by_source: { source: string; hits: number; dids: number; last_hit: string | null }[];
  by_owner: {
    usr_id: number | null; username: string | null; hits: number; dids: number;
    quarantined: number; released: number; still_active: number; hits_7d: number; last_hit: string | null;
  }[];
  by_type: { complaint_type: string; hits: number }[];
  by_state: { state: string; hits: number }[];
  weekly: { week_start: string; hits: number; dids: number }[];
  top_dids: {
    phone: string; did_id: number | null; username: string | null; owner_usr_id: number | null;
    hits: number; first_hit: string; last_hit: string; acquired_at: string | null;
    first_used_at: string | null; did_status: ComplaintHit["did_status"]; sources: string;
  }[];
}

export async function listComplaints(params: ComplaintListParams = {}): Promise<ComplaintList> {
  const resp = await api.get<APIResponse<ComplaintList>>("/dids/complaints", { params });
  return unwrap(resp.data);
}

export async function getComplaintSummary(
  params: Pick<ComplaintListParams, "owner" | "ours_only" | "days" | "source"> = {},
): Promise<ComplaintSummary> {
  const resp = await api.get<APIResponse<ComplaintSummary>>("/dids/complaints/summary", { params });
  return unwrap(resp.data);
}

export async function runComplaintCheck(): Promise<{ queued: boolean; fcc_task_id: string; ftc_task_id: string }> {
  const resp = await api.post<APIResponse<{ queued: boolean; fcc_task_id: string; ftc_task_id: string }>>(
    "/dids/complaints/check",
  );
  return unwrap(resp.data);
}
